import type { AmbientVideo as AmbientVideoData } from '@cartazo/contracts'
import { useEffect, useRef, useState } from 'react'

/**
 * Vídeo de ambiente sobre la portada de una categoría.
 *
 * Es una MEJORA, nunca el contenido. Debajo sigue estando la foto, que hace de
 * póster y es lo que se ve si el clip no llega — que con una barra de cobertura
 * es a menudo. Si esta capa fallara entera, la carta seguiría siendo correcta.
 *
 * Las reglas que lo hacen viable, en orden de importancia:
 *
 *  1. Presupuesto POR SESIÓN. Al agotarse se deja de pedir vídeo y todo sigue
 *     con fotos. Nadie percibe el cambio, porque el estado normal ya es la foto.
 *  2. `preload="none"`. El elemento no pide un solo byte hasta que decidimos.
 *  3. Solo la portada VISIBLE, y solo si el comensal se queda. Pasar de largo no
 *     debe costar nada.
 *  4. Tres interruptores que lo apagan entero: ahorro de datos, conexión lenta y
 *     preferencia de movimiento reducido.
 */

/**
 * Cuánto vídeo se permite en toda la sesión.
 *
 * Seis megas es aproximadamente el ambiente de cinco categorías. Es un tope
 * duro: no se reparte ni se renueva, porque el objetivo no es repartir bien sino
 * garantizar que la carta nunca se lleve por delante el plan de datos de nadie.
 */
const SESSION_BUDGET_BYTES = 6 * 1024 * 1024

/** Cuánto tiene que quedarse el comensal antes de gastar datos en el vídeo. */
const DWELL_MS = 900

/** Bytes ya comprometidos en esta sesión. Vive fuera de React: no se repinta. */
let spent = 0

/**
 * ¿Se puede gastar en vídeo ahora mismo?
 *
 * `saveData` es una petición explícita del comensal: respetarla no es opcional.
 * `effectiveType` cubre el caso más común del producto —el sótano con una barra—
 * donde el vídeo no llegaría a tiempo aunque lo pidiéramos.
 */
function allowedToSpend(bytes: number): boolean {
  if (spent + bytes > SESSION_BUDGET_BYTES) return false

  const connection = (
    navigator as Navigator & {
      connection?: { saveData?: boolean; effectiveType?: string }
    }
  ).connection

  if (connection?.saveData) return false
  if (connection?.effectiveType && /2g/.test(connection.effectiveType)) return false

  return !window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

export function AmbientVideo({
  video,
  active,
}: {
  video: AmbientVideoData | null
  /** `true` cuando esta portada es la que se está mirando. */
  active: boolean
}) {
  const ref = useRef<HTMLVideoElement>(null)
  const [src, setSrc] = useState<string | null>(null)

  useEffect(() => {
    if (!video || !active || src) return

    // La espera es la regla 3: quien pasa de largo no gasta nada.
    const timer = setTimeout(() => {
      if (!allowedToSpend(video.bytes)) return
      spent += video.bytes
      setSrc(video.src)
    }, DWELL_MS)

    return () => clearTimeout(timer)
  }, [video, active, src])

  // Fuera de pantalla se pausa. Un vídeo reproduciéndose en una portada que
  // nadie mira gasta batería y decodificador para nada.
  useEffect(() => {
    const element = ref.current
    if (!element || !src) return

    if (active) void element.play().catch(() => {})
    else element.pause()
  }, [active, src])

  if (!video || !src) return null

  return (
    <video
      ref={ref}
      className="category-panel__video"
      src={src}
      // `muted` y `playsInline` NO son opcionales: sin los dos, iOS se niega a
      // reproducir sin un gesto del usuario y el vídeo nunca arranca.
      muted
      playsInline
      loop
      preload="none"
      // El póster lo pone la foto que ya está debajo; duplicarlo aquí
      // descargaría la misma imagen dos veces.
      //
      // `aria-hidden` y `tabIndex={-1}` van SIEMPRE juntos. Un `<video>` puede
      // recibir el foco, y esconderlo de los lectores de pantalla sin sacarlo
      // del orden de tabulación crea lo peor de los dos mundos: quien navega con
      // teclado aterriza en un elemento del que su lector no dice nada.
      aria-hidden="true"
      tabIndex={-1}
      onError={() => setSrc(null)}
    />
  )
}
